import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  ChevronRightIcon,
  FileSearchIcon,
  FileTextIcon,
  MoreHorizontalIcon,
  PlusIcon,
  ScanTextIcon,
} from 'lucide-react';
import { PERMISSIONS } from '@siteflow/shared';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { DataTable, dataTableColumnHelper, type DataTableColumn } from '@/components/data-table';
import { EmptyState } from '@/components/empty-state';
import { PermissionGate } from '@/components/permission-gate';
import { ScanDialog } from '@/components/content/scan-dialog';
import { CONTENT_ONLY } from '@/lib/ui-mode';
import { StatusChip } from '@/components/status-chip';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { messageForError } from '@/lib/api-client';
import { pageKeys, pagesApi, pagesQuery } from '@/lib/api/pages';
import type { Page } from '@/lib/api/types';
import { useOrgPermissions } from '@/lib/permissions';
import { formatDate } from '@/lib/utils';

const helper = dataTableColumnHelper<Page>();

const pageSchema = z.object({
  slug: z.string().min(1, 'Give the page a path').max(500),
  name: z.string().min(1, 'Give the page a name').max(200),
  description: z.string().max(1000).optional(),
});
type PageValues = z.infer<typeof pageSchema>;

function PagesList() {
  const { orgId, siteId } = Route.useParams();
  const queryClient = useQueryClient();
  const { has } = useOrgPermissions(orgId);
  const canEdit = has(PERMISSIONS.CONTENT_EDIT);

  const pages = useQuery(pagesQuery(orgId, siteId));

  const [createOpen, setCreateOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [editing, setEditing] = useState<Page | null>(null);
  const [deleting, setDeleting] = useState<Page | null>(null);

  const form = useForm<PageValues>({
    resolver: zodResolver(pageSchema),
    defaultValues: { slug: '', name: '', description: '' },
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: pageKeys.all(orgId, siteId) });
  }

  const createMutation = useMutation({
    mutationFn: (values: PageValues) =>
      pagesApi.create(orgId, siteId, {
        slug: values.slug,
        name: values.name,
        ...(values.description ? { description: values.description } : {}),
      }),
    onSuccess: (page) => {
      toast.success(`${page.name} registered`);
      setCreateOpen(false);
      form.reset({ slug: '', name: '', description: '' });
      invalidate();
    },
    onError: (error) => toast.error(messageForError(error, 'That page could not be registered.')),
  });

  const updateMutation = useMutation({
    mutationFn: ({ pageId, values }: { pageId: string; values: PageValues }) =>
      pagesApi.update(orgId, siteId, pageId, {
        name: values.name,
        description: values.description || null,
      }),
    onSuccess: () => {
      toast.success('Page updated');
      setEditing(null);
      invalidate();
    },
    onError: (error) => toast.error(messageForError(error, 'That page could not be updated.')),
  });

  const deleteMutation = useMutation({
    mutationFn: (pageId: string) => pagesApi.remove(orgId, siteId, pageId),
    onSuccess: () => {
      toast.success('Page removed');
      setDeleting(null);
      invalidate();
    },
    onError: (error) => toast.error(messageForError(error, 'That page could not be removed.')),
  });

  const columns = useMemo<Array<DataTableColumn<Page>>>(
    () =>
      helper.columns([
        helper.accessor('name', {
          header: 'Page',
          cell: (info) => {
            const page = info.row.original;
            return (
              <div className="min-w-0">
                <Link
                  to="/app/$orgId/sites/$siteId/pages/$pageId"
                  params={{ orgId, siteId, pageId: page.id }}
                  className="font-medium text-ink underline-offset-2 hover:underline"
                >
                  {page.name}
                </Link>
                {CONTENT_ONLY ? null : (
                  <span className="block truncate font-mono text-micro text-ink-subtle">
                    {page.slug}
                  </span>
                )}
              </div>
            );
          },
        }),
        helper.accessor('description', {
          header: 'Description',
          cell: (info) => <span className="text-ink-muted">{info.getValue() ?? '—'}</span>,
        }),
        helper.accessor('status', {
          header: 'Status',
          cell: (info) =>
            info.getValue() === 'active' ? (
              <StatusChip tone="active">Active</StatusChip>
            ) : (
              <StatusChip tone="neutral">Archived</StatusChip>
            ),
        }),
        helper.accessor('createdAt', {
          header: 'Registered',
          cell: (info) => (
            <span className="text-ink-muted" data-numeric>
              {formatDate(info.getValue())}
            </span>
          ),
        }),
        helper.display({
          id: 'actions',
          header: () => <span className="sr-only">Actions</span>,
          cell: (info) => {
            const page = info.row.original;
            if (!canEdit) return null;
            return (
              <div className="flex justify-end">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="iconSm" aria-label={`Actions for ${page.name}`}>
                      <MoreHorizontalIcon />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onSelect={() => {
                        setEditing(page);
                        form.reset({
                          slug: page.slug,
                          name: page.name,
                          description: page.description ?? '',
                        });
                      }}
                    >
                      Rename
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem destructive onSelect={() => setDeleting(page)}>
                      Remove page
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          },
        }),
      ]),
    [canEdit, form, orgId, siteId],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="max-w-prose">
          <h2 className="text-lede font-semibold tracking-tight text-ink">Pages</h2>
          <p className="text-data leading-relaxed text-ink-muted">
            {CONTENT_ONLY
              ? 'Choose a page to change its text.'
              : 'A page here mirrors a real URL on the site. Registering it groups the entries that belong to that URL, so an editor opens one screen rather than hunting for keys.'}
          </p>
        </div>
        {CONTENT_ONLY ? null : (
          <PermissionGate permission={PERMISSIONS.CONTENT_EDIT}>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setScanOpen(true)}>
                <ScanTextIcon />
                Scan for content keys
              </Button>
              <Button onClick={() => setCreateOpen(true)}>
                <PlusIcon />
                Register page
              </Button>
            </div>
          </PermissionGate>
        )}
      </div>

      {!pages.isLoading && (pages.data?.length ?? 0) === 0 && !pages.isError ? (
        <EmptyState
          icon={FileSearchIcon}
          title={CONTENT_ONLY ? 'No pages to edit yet' : 'No pages registered'}
          description={
            CONTENT_ONLY
              ? 'Pages appear here once your administrator has set them up.'
              : 'Register the URLs whose copy editors should own — a home page, a pricing page, an about page. Each one collects the content keys found on that URL.'
          }
          action={
            CONTENT_ONLY ? undefined : (
              <PermissionGate permission={PERMISSIONS.CONTENT_EDIT}>
                <Button onClick={() => setCreateOpen(true)}>Register the first page</Button>
              </PermissionGate>
            )
          }
        />
      ) : CONTENT_ONLY ? (
        <PageRows
          orgId={orgId}
          siteId={siteId}
          pages={pages.data ?? []}
          isLoading={pages.isLoading}
          error={
            pages.isError
              ? messageForError(pages.error, 'The page list could not be loaded.')
              : null
          }
        />
      ) : (
        <DataTable
          columns={columns}
          data={pages.data ?? []}
          isLoading={pages.isLoading}
          getRowId={(row) => row.id}
          caption="Registered pages"
          error={
            pages.isError ? (
              <span className="text-data text-danger">
                {messageForError(pages.error, 'The page list could not be loaded.')}
              </span>
            ) : undefined
          }
        />
      )}

      <ScanDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        orgId={orgId}
        siteId={siteId}
        pageSlug={null}
        canRegister={canEdit}
      />

      <Dialog
        open={createOpen || editing !== null}
        onOpenChange={(open) => {
          if (open) return;
          setCreateOpen(false);
          setEditing(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Rename page' : 'Register a page'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'The path cannot change: entries are attached to it. Remove and re-register the page if the URL moved.'
                : 'Use the path exactly as it appears on the site. A leading slash is added for you and trailing slashes are dropped.'}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form
              noValidate
              onSubmit={form.handleSubmit((values) =>
                editing
                  ? updateMutation.mutate({ pageId: editing.id, values })
                  : createMutation.mutate(values),
              )}
            >
              <DialogBody className="flex flex-col gap-4">
                <FormField
                  control={form.control}
                  name="slug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Path</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="/about"
                          className="font-mono"
                          disabled={editing !== null}
                          {...field}
                        />
                      </FormControl>
                      <FormDescription>Use / for the home page.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input placeholder="About" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea rows={2} {...field} />
                      </FormControl>
                      <FormDescription>
                        Optional note for whoever edits this page next.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </DialogBody>

              <DialogFooter>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setCreateOpen(false);
                    setEditing(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {editing ? 'Save changes' : 'Register page'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        destructive
        title="Remove this page?"
        confirmLabel="Remove page"
        pending={deleteMutation.isPending}
        description={
          <>
            <strong className="text-ink">{deleting?.name}</strong> and the entries attached to it
            are soft-deleted, so the dashboard stops showing them. Anything already published stays
            on the live site until you unpublish it.
          </>
        }
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
      />
    </div>
  );
}

/** Pages as soft rows, each led by a round icon, for the site owner's editor. */
function PageRows({
  orgId,
  siteId,
  pages,
  isLoading,
  error,
}: {
  orgId: string;
  siteId: string;
  pages: Page[];
  isLoading: boolean;
  error: string | null;
}) {
  if (error) {
    return (
      <p role="alert" className="text-data text-danger">
        {error}
      </p>
    );
  }
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="h-[4.5rem] animate-pulse rounded-[22px] bg-elevated" />
        ))}
      </div>
    );
  }
  return (
    <ul className="flex flex-col gap-3" aria-label="Pages">
      {pages.map((page) => (
        <li key={page.id}>
          <Link
            to="/app/$orgId/sites/$siteId/pages/$pageId"
            params={{ orgId, siteId, pageId: page.id }}
            className="group flex items-center gap-4 rounded-[22px] border border-hairline bg-elevated px-5 py-4 transition-[box-shadow,border-color] duration-150 [box-shadow:var(--cs-card-shadow)] hover:border-hairline-strong"
          >
            <span className="cs-badge" aria-hidden="true">
              <FileTextIcon className="size-[1.1rem]" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-body font-semibold tracking-tight text-ink">
                {page.name}
              </span>
              {page.description ? (
                <span className="block truncate text-data text-ink-muted">{page.description}</span>
              ) : null}
            </span>
            <ChevronRightIcon
              aria-hidden="true"
              className="size-5 shrink-0 text-ink-subtle transition-transform duration-150 group-hover:translate-x-0.5 motion-reduce:transition-none"
            />
          </Link>
        </li>
      ))}
    </ul>
  );
}

export const Route = createFileRoute('/app/$orgId/sites/$siteId/pages/')({
  component: PagesList,
});
